import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux"
import debounce from 'lodash.debounce';
import { Button, CloseButton, Input, InputGroup, InputLeftElement, InputRightElement, useDisclosure } from "@chakra-ui/react";
import InputField from "components/fields/InputField";
import Modal from "components/modal/Modal";
import { FaPlusCircle, FaTrashAlt } from "react-icons/fa";
import TooltipComponent from "components/tooltip";
import { FiSearch } from "react-icons/fi";
import ButtonPrimary from "components/button/ButtonPrimary";
import { IoAddCircle, IoSearch } from "react-icons/io5";
import { SET_TO_DEFAULT_RESPONSE, create, del, getByPage } from "./../../../store/features/slices/clienteSlice";
import { message as alerta } from "utils/Funciones";
import { uriSerialized } from "utils/uriSerialized";
import { alertaConfirmar } from "utils/Funciones";
import AutoComplete from "components/autocomplete/AutoComplete";
import { validarDNI } from "utils/Funciones";
import { validarRUC } from "utils/Funciones";
import { getByDocumentId } from "store/features/slices/consultaReniecSlice";
import Spinner from "components/spinner/Spinner";
import { SET_TO_DEFAULT_RESPONSE_CONSULTA_RENIEC } from "store/features/slices/consultaReniecSlice";
import { validarEMAIL } from "utils/Funciones";
import TablePagination from "./table/TablePagination";

const Clientes = () => {
    const dispatch = useDispatch();
    const { userToken } = useSelector((state) => state.userReducer);
    const {
        dataByPage,
        totalPage,
        totalRow,
        isMutation
    } = useSelector((state) => state.clienteReducer);

    const {
        isLoading: isLoadingConsultaReniec,
        responseGetByDocumentId
    } = useSelector((state) => state.consultaReniecReducer);

    const registrar = useDisclosure()
    const editar = useDisclosure()
    const [searchTerm, setSearchTerm] = useState('');
    const [state, setState] = useState({
        cliente: {
            dniruc: '',
            nombres: '',
            nombrecompleto: '',
            ap_paterno: '',
            ap_materno: '',
            direccion: '',
            correo: '',
            telefono: '',
            estado: '',
            condicion: '',
            tipodocumento: '',
        }
    })

    const [stateEdit, setStateEdit] = useState({
        _id: '',
        name: '',
        disabled: false
    })

    const setDefaultState = () => {
        setState({
            ...state,
            cliente: {
                dniruc: '',
                nombres: '',
                nombrecompleto: '',
                ap_paterno: '',
                ap_materno: '',
                direccion: '',
                correo: '',
                telefono: '',
                estado: '',
                condicion: '',
                tipodocumento: '',
            }
        })
    }

    const columnsDataColumns = useMemo(
        () => [
            {
                Header: "Documento",
                accessor: "dniruc",
                type: 'String'
            }, {
                Header: "Tipo documento",
                accessor: "tipodocumento",
                type: 'String'
            }, {
                Header: "Nombre / Razon social",
                accessor: 'dataruc.razonsocial',
                type: 'String'
            }, {
                Header: "Estado contribuyente",
                accessor: "dataruc.estado",
                type: 'String'
            }, {
                Header: "Condicion contribuyente",
                accessor: "dataruc.condicion",
                type: 'String'
            }, {
                Header: "F. registro",
                accessor: "createdAt",
                type: 'Date'
            },
            {
                Header: "Opciones",
                accessor: "opciones",
                type: 'opciones',
                disableSortBy: true,
                Cell: ({ value }) => (
                    <div className="flex gap-5">
                        <TooltipComponent
                            trigger={<Button onClick={() => handleDelete(value)} colorScheme={'red'} className="rounded-xl bg-red-500 !px-1 !py-1 text-base font-medium text-white transition duration-200 hover:bg-red-600 active:bg-red-700 dark:bg-red-400 dark:text-white dark:hover:bg-red-300 dark:active:bg-red-200">
                                <FaTrashAlt className="w-4 h-4 text-white" />
                            </Button>}
                            content={
                                <p className="dark:text-white">
                                    eliminar
                                </p>
                            }
                        />
                    </div>
                )
            },
        ],
        []
    );

    function handleEdit(row) {
        setStateEdit({
            ...stateEdit,
            _id: row._id,
            name: row.name,
            disabled: row.disabled
        })
        editar.onOpen()
    }

    function handleDelete(row) {
        alertaConfirmar('Eliminar Cliente', `Estas seguro que quieres eliminar el cliente, <b>${row.tipodocumento === 'RUC' ? row.dataruc.razonsocial : row.datadni.nombrecompleto}</b>? recuerda que se eliminarán todos los datos relacionados a este.`, 'question', async (res_alert) => {
            if (res_alert === true) {
                await dispatch(del(row._id));
            }
        })
    }

    const onSubmitRegistrar = async e => {
        e.preventDefault();
        if (state.cliente.nombrecompleto.trim().length < 1) {
            alerta('nombre o razón social es requerido', 'error', 4)
        } else if (state.cliente.tipodocumento.trim().length < 1) {
            alerta('tipo de documento es requerido', 'error', 4)
        } else if (state.cliente.correo.trim().length > 0 && validarEMAIL(state.cliente.correo)) {
            alerta('correo no válido', 'error', 4)
        } else {
            const formData = new FormData();
            formData.append('data', JSON.stringify(state));
            await dispatch(create(formData));
        }
    }

    useEffect(() => {
        if (isMutation.success) {
            alerta(isMutation.message, 'success', 5)
            if (registrar.isOpen) {
                registrar.onClose()
                setDefaultState()
            }
            dispatch(SET_TO_DEFAULT_RESPONSE())
        }
    }, [isMutation.success])

    useEffect(() => {
        if (isMutation.updated) {
            alerta(isMutation.message, 'success', 5)
            if (editar.isOpen) {
                editar.onClose()
                setDefaultState()
            }
            dispatch(SET_TO_DEFAULT_RESPONSE())
        }
    }, [isMutation.updated])

    const fetchData = useCallback(
        (pageSize, pageIndex, search, order) => {
            const queryOptions = {
                page: pageIndex,
                limit: pageSize,
                search: search,
                order: order,
            }
            const query = queryOptions ? "?" + uriSerialized(queryOptions) : ""
            dispatch(getByPage(query))
        },
        [isMutation.success]
    )

    const onChangeSearchTerm = e => {
        setSearchTerm(e.target.value);
    };

    const onChangeSearchTermClear = e => {
        setSearchTerm("");
        document.getElementById("search").value = "";
    };

    const debounceSearchTerm = useMemo(() => debounce(onChangeSearchTerm, 700), []);

    // CONSULTA RENIEC

    const onSubmitConsultarReniec = async () => {
        if (state.cliente.tipodocumento.trim().length === 0) {
            alerta('Selecciona un tipo de documento', 'error', '5')
        } else if (state.cliente.tipodocumento === 'DNI' && !validarDNI(state.cliente.dniruc)) {
            alerta('DNI inválido', 'error', '5')
        } else if (state.cliente.tipodocumento === 'RUC' && !validarRUC(state.cliente.dniruc)) {
            alerta('RUC inválido', 'error', '5')
        } else {
            const queryOptions = {
                number: state.cliente.dniruc,
                type: state.cliente.tipodocumento,
            }
            const query = queryOptions ? "?" + uriSerialized(queryOptions) : ""
            await dispatch(getByDocumentId({ query: query, token: userToken }))
        }
    }

    useEffect(() => {
        if (responseGetByDocumentId) {
            if (state.cliente.tipodocumento === 'RUC') {
                setState({
                    ...state, cliente:
                    {
                        ...state.cliente,
                        nombrecompleto: responseGetByDocumentId.razonsocial,
                        direccion: responseGetByDocumentId.direccion,
                        departamento: responseGetByDocumentId.departamento,
                        provincia: responseGetByDocumentId.provincia,
                        distrito: responseGetByDocumentId.distrito,
                        estado: responseGetByDocumentId.estado,
                        condicion: responseGetByDocumentId.condicion,
                    }
                })
            } else {
                setState({
                    ...state, cliente:
                    {
                        ...state.cliente,
                        nombrecompleto: responseGetByDocumentId.apellidoPaterno + ' ' + responseGetByDocumentId.apellidoMaterno + ' ' + responseGetByDocumentId.nombres,
                        nombres: responseGetByDocumentId.nombres,
                        ap_paterno: responseGetByDocumentId.apellidoPaterno,
                        ap_materno: responseGetByDocumentId.apellidoMaterno,
                        direccion: responseGetByDocumentId.direccion
                    }
                })
            }
        }
        dispatch(SET_TO_DEFAULT_RESPONSE_CONSULTA_RENIEC())
    }, [responseGetByDocumentId])

    return (
        <div className="mt-4 md:mt-0">
            <div className="sticky z-30 w-full top-44 md:top-28 flex items-center justify-end bg-white dark:!bg-navy-800 p-2 rounded-xl shadow-xl shadow-gray-200 dark:shadow-none">
                <div className="relative flex h-[61px] w-[calc(100%-55px)] flex-grow items-center gap-2 rounded-full bg-white px-0 py-2 dark:!bg-navy-800">
                    <InputGroup className="!w-full md:!w-[300px]">
                        <InputLeftElement pointerEvents="none" className="!mt-1 !ml-1">
                            <FiSearch size={24} className="text-navy-500 dark:text-navy-300" />
                        </InputLeftElement>
                        <Input size='lg' autoFocus id="search" onChange={debounceSearchTerm} type="text" placeholder={`Buscar ${totalRow} clientes...`} className="flex w-full items-center justify-center !rounded-xl border !bg-lightPrimary 
            dark:!bg-navy-900 !text-sm !ring-none focus:!ring-brand-500 
            focus:!border-brand-500 dark:focus:!ring-brand-400 dark:focus:!border-brand-400 
            !outline-none !border-gray-200 dark:!border-white/10 dark:text-white" />
                        <InputRightElement className="!mt-1">
                            <CloseButton onClick={onChangeSearchTermClear} size='md' className={`mr-2 ${searchTerm.trim().length > 0 ? 'scale-100' : 'scale-0'} transition-all duration-500 text-navy-500 dark:text-navy-300`} />
                        </InputRightElement>
                    </InputGroup>
                </div>
                <div className="w-[55px] h-[61px] flex items-center">
                    <TooltipComponent
                        trigger={<button onClick={registrar.onOpen} className="bg-brand-500 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200 flex items-center justify-center rounded-full p-3 text-3xl text-white transition duration-200 hover:cursor-pointer dark:text-white">
                            <FaPlusCircle />
                        </button>}
                        content={
                            <p className="dark:text-white">
                                registrar cliente
                            </p>
                        }
                    />
                </div>
            </div>
            <div className="mt-3 md:mt-8">

                {/** REGISTRAR */}
                <Modal isOpen={registrar.isOpen} onClose={registrar.onClose} headerTitle='Registrar cliente'>
                    <div className="px-[5px] md:px-[10px] pt-[20px] pb-[20px] max-w-[1368px] flex flex-col !z-[1004]">
                        <form className="w-full" onSubmit={onSubmitRegistrar}>
                            <div className="flex flex-wrap">
                                <AutoComplete
                                    autoFocus={true}
                                    extra="w-full md:w-4/12 mb-3 p-2"
                                    label="Tipo de documento"
                                    data={[
                                        { tipo: 'DNI' }, { tipo: 'RUC' },
                                        { tipo: 'CARNET DE EXTRANJERIA' }, { tipo: 'PASAPORTE' }
                                    ]}
                                    value={state.cliente.tipodocumento}
                                    required={true}
                                    labelProp="tipo"
                                    valueProp="tipo"
                                    onChange={(val) => setState({ ...state, cliente: { ...state.cliente, tipodocumento: val } })}
                                />
                                <InputField
                                    variant="input"
                                    required={true}
                                    disabled={isLoadingConsultaReniec}
                                    iconright={<IoSearch className="text-brand-600 dark:text-brand-400" size={!24} />}
                                    onClickIconRight={onSubmitConsultarReniec}
                                    extra="w-full md:w-4/12 mb-3 p-2"
                                    label="Número de documento"
                                    placeholder="Número de documento"
                                    id="dniruc"
                                    type="text"
                                    name="dniruc"
                                    onChange={(e) => setState({ ...state, cliente: { ...state.cliente, dniruc: e.target.value } })}
                                    value={state.cliente.dniruc}
                                />

                                <InputField
                                    variant="input"
                                    required={true}
                                    iconLoading={<Spinner size="w-[25px] h-[25px]" />}
                                    disabled={isLoadingConsultaReniec}
                                    extra="w-full md:w-4/12 mb-3 p-2"
                                    label={`${state.cliente.tipodocumento === 'RUC' ? 'Razón social' : 'Nombre completo'}`}
                                    placeholder=""
                                    id="nombrecompleto"
                                    type="text"
                                    name="nombrecompleto"
                                    onChange={(e) => setState({ ...state, cliente: { ...state.cliente, nombrecompleto: e.target.value } })}
                                    value={state.cliente.nombrecompleto}
                                />

                                <InputField
                                    variant="input"
                                    required={true}
                                    extra="w-full md:w-4/12 mb-3 p-2"
                                    label={`${state.cliente.tipodocumento === 'RUC' ? 'Dirección fiscal' : 'Dirección'}`}
                                    placeholder=""
                                    id="direccion"
                                    type="text"
                                    name="direccion"
                                    onChange={(e) => setState({ ...state, cliente: { ...state.cliente, direccion: e.target.value } })}
                                    value={state.cliente.direccion}
                                />

                                <InputField
                                    variant="input"
                                    required={false}
                                    extra="w-full md:w-4/12 mb-3 p-2"
                                    label="Correo"
                                    placeholder="Correo"
                                    id="correo"
                                    type="email"
                                    name="correo"
                                    onChange={(e) => setState({ ...state, cliente: { ...state.cliente, correo: e.target.value } })}
                                    value={state.cliente.correo}
                                />

                                <InputField
                                    variant="input"
                                    required={false}
                                    extra="w-full md:w-4/12 mb-3 p-2"
                                    label="Celular"
                                    placeholder="Celular"
                                    id="telefono"
                                    type="text"
                                    name="telefono"
                                    onChange={(e) => setState({ ...state, cliente: { ...state.cliente, telefono: e.target.value } })}
                                    value={state.cliente.telefono}
                                />

                                {
                                    state.cliente.tipodocumento === 'RUC' &&
                                    <>
                                        <InputField
                                            variant="input"
                                            readOnly={true}
                                            required={false}
                                            extra="w-full md:w-4/12 mb-3 p-2"
                                            label="Estado"
                                            placeholder="Estado"
                                            id="estado"
                                            type="text"
                                            name="estado"
                                            value={state.cliente.estado}
                                        />

                                        <InputField
                                            variant="input"
                                            readOnly={true}
                                            required={false}
                                            extra="w-full md:w-4/12 mb-3 p-2"
                                            label="Condición"
                                            placeholder="Condición"
                                            id="condicion"
                                            type="text"
                                            name="condicion"
                                            value={state.cliente.condicion}
                                        />
                                    </>
                                }


                                <ButtonPrimary isLoading={isMutation.loading} disabled={false} icon={<IoAddCircle size={28} />}>
                                    Registrar
                                </ButtonPrimary>
                            </div>
                        </form>
                    </div>
                </Modal>

                <div className="mt-5 grid h-full grid-cols-1">

                    <TablePagination
                        searchTerm={searchTerm}
                        tableData={dataByPage}
                        fetchData={fetchData}
                        loading={isMutation.loading}
                        pageCount={totalPage}
                        totalRow={totalRow}
                        actions={{
                            onDelete: (item) => handleDelete(item)
                        }}
                    />
                </div>
            </div>
        </div >

    );
};

export default Clientes;
